import { WalletApi } from 'lucid-txpipe';
import { z } from 'zod';

export type PortfolioMetadata = {
  manager: {
    id: string;
    name: string;
    imageUrl: string;
    address: string;
    joinDate: Date;
    description: string;
    verified: boolean;
  };
  id: string;
  portfolioId: string;
  about: string;
  objective: string;
  strategy: string;
};

export interface WalletData {
  wallet: WalletApi;
}

export type CancelOrder = {
  id: string;
  address: string;
};

/** Schema for the response of an order cancellation */
export const cancelOrderResponseSchema = z.string();
export type CancelOrderResponse = z.infer<typeof cancelOrderResponseSchema>;

export const faucetResponseSchema = z.string();
export type faucetResponseSchema = z.infer<typeof faucetResponseSchema>;

export const faucetRequestSchema = z
  .object({
    /**
     * Address of the user that requests tokens from the faucet
     */
    address: z.string(),
    /**
     * Unit of token requested and amount of tokens to withdraw
     */
    tokens: z.record(z.bigint().positive()),
  })
  .strict();
export type FaucetRequestSchema = z.infer<typeof faucetRequestSchema>;

export type SwapSchema = {
  address: string;
  portfolioId: string;
  maxBatcherFee: bigint;
  totalAmount: bigint;
  userAssets: [string, bigint][];
  swap: [string, bigint][];
  utxos?:
    | {
        address: string;
        txHash: string;
        outputIndex: number;
        assets: Record<string, bigint>;
      }[]
    | undefined;
};

export type Withdraw = {
  address: string;
  portfolioId: string;
  tokens: [string, bigint][];
  maxBatcherFee: bigint;
  amount: bigint;
  minWorthAcceptable: bigint;
};

export type Deposit = {
  address: string;
  portfolioId: string;
  tokens: [string, bigint][];
  minMtkAcceptable: bigint;
  maxBatcherFee: bigint;
};

export const createSwapResponseSchema = z.object({
  cbor: z.string({ description: 'Unsigned transaction cbor' }),
  txId: z.string({ description: 'Transaction hash' }),
});

export const createOrderResponseSchema = z
  .object({ cbor: z.string(), id: z.string() })
  .strict();
export type CreateOrderResponse = z.infer<typeof createOrderResponseSchema>;

export const submitSwapSchema = z.object({
  cbor: z.string(),
});
export type SubmitSwap = z.infer<typeof submitSwapSchema>;

export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export type Portfolio = {
  id: string;
  name: string;
  ticker: string;
  imageUrl: string;
  createdAt: Date;
  policyId: string;
  onchainId: string;
  mtkUnit: string;
  featured: bigint;
  managerId: string;
};

export type Asset = {
  id: string;
  name: string;
  ticker: string;
  imageUrl: string;
  currencySymbol: string;
  mainnetUnit: string;
  mainnetFingerprint: string;
  decimals: number;
  riskScore: string;
  pairConversion: string;
};
export interface IAssetInPortfolio {
  asset: Asset;
  price: string;
  priceCreatedAt: Date;
  priceId: string;
  amount: bigint;
  weightNum: bigint;
  weightDenom: bigint;
  order: number;
}

export interface IPortfolioState {
  portfolio: Portfolio;
  price: string;
  supply: bigint;
  platformFee: bigint;
  assets: IAssetInPortfolio[];
  entryFee: bigint;
  exitFee: bigint;
  batcherFee: bigint;
}

export enum OrderType {
  /**
   * Order is a deposit of liquidity to the portfolio.
   *
   * Can be placed by anyone
   */
  DEPOSIT = 'deposit',
  /**
   * Order is a withdraw of liquidity from the portfolio.
   *
   * Can be placed by anyone
   */
  WITHDRAW = 'withdraw',
  /**
   * Order updates the target weights of the tokens of a portfolio

   * Can be placed by the Portfolio Manager
   */
  WEIGHTS = 'weights',
  /**
   * Order updates the lovelace amount for processing each user order
   *
   * Can be placed by Metera Admin (Processor)
   */
  BATCHER_FEE = 'batcher_fee',
  /**
   * Order updates the fee percentage that the platform takes when an order is placed
   *
   * Can be placed by the Treasury
   */
  PLATFORM_FEE = 'platform_fee',
  /**
   * Order updates the fee percentage the user must pay to the Portfolio Manager for interacting with the Portfolio
   *
   * Can be placed by the Portfolio Manager
   */
  USER_FEE = 'user_fee',
  /**
   * Order that extracts MTK accumulated by user interactions (deposits/withdraws)
   *
   * Can be placed by the Portfolio Manager
   */
  COLLECT = 'collect',
}

export type Order = {
  id: string;
  placeOrderTxHash: string;
  processOrderTxHash: string | null;
  walletId: string;
  amount: bigint;
  orderPlacedAt: Date | null;
  orderProcessedAt: Date | null;
  expiredAt: Date | null;
  createdAt: Date | null;
  placedSubmittedAt: Date | null;
  processSubmittedAt: Date | null;
  price: string;
  portfolioStatusId: string;
  userFee: bigint;
  platformFee: bigint;
  batcherFee: bigint;
  cancelSubmittedAt: Date | null;
  cancelledAt: Date | null;
  cancelHash: string | null;
  slippage: bigint;
  slippageFail: boolean;
  type: OrderType;
  utxoIndex: number;
  validationFailures: string | null;
};

export type PortfolioManager = {
  id: string;
  name: string;
  joinDate: Date;
  description: string;
  imageUrl: string;
  verified: boolean;
  address: string;
};

export interface MintModalBalances {
  [key: string]: {
    asset: IAssetInPortfolio['asset'];
    // Tokens currently in wallet
    owned: bigint;
    // Tokens required to deposit
    required: bigint;
    // Tokens that will be swapped on Sundae
    deficit: bigint;
  };
}

import { createContext } from 'react';

export interface WalletData {
  wallet: WalletApi;
}

export const WalletContext = createContext<WalletData | null | undefined>(
  undefined,
);
